// VqeDdlEditContext — shared edit-context shape passed down from
// `VisualDdlWorkspace` (or the Compact DDL editor's `compactDdlBodyArm`) to
// individual render components that surface DDL edit affordances.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.2 — Commit Pipeline + Buffer Sync.
//
// Mirrors VQE-Edit's `editContext.ts` (the analog DML consumer); extends it
// with an `objectKind` classification so render components can dispatch on
// the kind of DDL object the cursor is parked in (table / view / sequence /
// trigger / etc.). Render components treat this prop as **optional** — when
// undefined, the existing disabled scaffolding renders unchanged. This keeps
// VqeDdlEdit's read-only unit tests passing without per-test stubbing.

import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';
import type {
  BlastRadiusGate,
  CommitDdlEditCtx,
  MonacoEditorLike,
  MonacoTextModelLike,
  VqeDdlPendingEdit,
} from './commitDdlEdit';

/** Top-level DDL object kind under the cursor / Designer surface. The render
 *  layer dispatches to the matching `render/<kind>/…Editor.tsx` component.
 *  Extends as later phases enable the remaining surfaces. */
export type DdlObjectKind =
  | 'table'
  | 'view'
  | 'materialized-view'
  | 'sequence'
  | 'type'
  | 'domain'
  | 'schema'
  | 'trigger'
  | 'routine'
  | 'package'
  | 'security'
  | 'database'
  | 'index'
  | 'drop';

export type VqeDdlEditContext = {
  readonly service: SqlResolverService;
  readonly documentId: string;
  readonly statementId: string;
  readonly model: MonacoTextModelLike;
  readonly editor?: MonacoEditorLike;
  readonly setPendingEdit?: (pending: VqeDdlPendingEdit) => void;
  /**
   * Top-level object classification — drives render-component dispatch
   * (`render/table/`, `render/view/`, …). Resolved per-statement by the
   * caller; `null` when the cursor sits in a non-DDL statement or the kind
   * is not yet supported.
   */
  readonly objectKind: DdlObjectKind | null;
  /**
   * Real connection id (saved-connection UUID), distinct from
   * `service.connectionId` which may be a synthetic per-tab placeholder.
   * Required by non-buffer actions (e.g. matview refresh — Slice D8) that
   * dispatch SQL directly against the live connection. `null` when the
   * editor tab is not bound to a saved connection.
   */
  readonly connectionId?: string | null;
  /** Active database name for `connectionId` — used by non-buffer actions
   *  that need to scope the dispatched statement. `null` when unset. */
  readonly database?: string | null;
  /** Slice H3 pre-destructive guard wiring. When supplied, drop ops with
   *  enumerated dependents stage a `VqeDdlPendingBlastRadius` instead of
   *  applying. Hosts that don't carry a dependency snapshot omit this; the
   *  guard then no-ops. */
  readonly blastRadius?: BlastRadiusGate;
};

/** Build the `commitDdlEdit` ctx slice from a VqeDdlEditContext. */
export function toCommitCtx(ctx: VqeDdlEditContext): CommitDdlEditCtx {
  return {
    service: ctx.service,
    connectionId: ctx.connectionId,
    database: ctx.database,
    documentId: ctx.documentId,
    statementId: ctx.statementId,
    model: ctx.model,
    editor: ctx.editor,
    setPendingEdit: ctx.setPendingEdit,
    blastRadius: ctx.blastRadius,
  };
}
